import { markRaw } from 'vue';
import { defineStore } from 'pinia';
import { auth } from '@/firebase';
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  signOut,
} from 'firebase/auth';
import { collection, onSnapshot, where, query, documentId, doc, getDoc } from 'firebase/firestore';
import { firestore } from '@/firebase';

export const useUserStore = defineStore('user', {
  state: () => ({
    user: null,
    authIsReady: false,
    unsubs: [],
    users: {},
    currentUser: null
  }),
  persist: { paths: ['users', 'currentUser'] },
  getters: {
    permissions(state) {
      return state.currentUser?.permissions ?? [];
    },
    hasPermission() {
      return (perm) => {
        const perms = this.permissions;
        return Array.isArray(perms) && perms.includes(perm);
      };
    },
    userId(state) {
      return state.user?.uid || null;
    }
  },
  actions: {
    async login({ email, password }) {
      const res = await signInWithEmailAndPassword(auth, email, password);
      if (res) {
        this.setUser(res.user);
      } else {
        throw new Error('Could not complete login');
      }
    },
    async loginWithGoogle() {
      const provider = new GoogleAuthProvider();
      const res = await signInWithPopup(auth, provider);
      if (res) {
        this.setUser(res.user);
      } else {
        throw new Error('Could not complete login');
      }
    },
    async logout() {
      await signOut(auth);
    },
    async getCurrentUser() {
      const userDocQuery = doc(firestore, "users", this.user.uid);
      let userDoc;
      userDoc = await getDoc(userDocQuery);
      this.currentUser = userDoc.data();
    },
    upsert({ id, value }) {
      this.users = { ...this.users, [id]: value };
    },
    remove(id) {
      const users = { ...this.users };
      delete users[id];
      this.users = users;
    },
    setAuthIsReady(value) {
      this.authIsReady = value;
    },
    setUser(user) {
      this.user = user ? markRaw(user) : null;
    },
    clearUnsubs() {
      for (const unsub of this.unsubs) {
        if (typeof unsub === 'function') unsub();
      }
      this.unsubs = [];
    },
    clear() {
      this.clearUnsubs();
      this.users = {};
      this.currentUser = null;
    },
    canListTeamMembers() {
      return this.hasPermission('users:manage') ||
        this.hasPermission('appointments:manage') ||
        this.hasPermission('appointmentRequests:manage');
    },
    async registerSnapshot() {
      this.clearUnsubs();
      await this.getCurrentUser();
      let snapshotQuery = collection(firestore, 'users');
      if (!this.canListTeamMembers()) {
        snapshotQuery = query(snapshotQuery, where(documentId(), '==', this.user.uid));
      }
      const unsub = onSnapshot(
        snapshotQuery,
        (snapshot) => {
          snapshot.docChanges().forEach((element) => {
            const elementData = { ...element.doc.data() };
            elementData.id = element.doc.id;
            if (element.type === 'added' || element.type === 'modified') {
              this.upsert({ id: element.doc.id, value: elementData });
            } else {
              this.remove(element.doc.id);
            }
          });
        },
        (error) => {
          console.error('User - Error registering snapshot', error);
        }
      );
      this.unsubs.push(unsub);
    }
  }
});
